import { Alert, Button, Input, Spin } from "antd"
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import { AppMain } from "components/layout/MainLayout"

import {
  fetchHasPassword,
  postProfilePassword,
} from "../actions/profilePassword"
import MainMenu from "../components/molecules/MainMenu"
import { PASSWORD_MIN_LENGTH, passwordProblem } from "../helpers/passwordAuth"
import { t } from "../lib/i18n"
import useAuth from "../recoils/atoms/useAuth"
import routes from "../router/routes"

// ログインとセキュリティ（#403）。Google / GitHub / Figma だけでログインしているユーザーが
// パスワードを足す、または設定済みのパスワードを変える画面。
//
// ログイン中であること（トークン）を email の所有確認とみなす（auth-api #1224）。
// 設定済みのときは現在のパスワードが必要（API と同じ）。
function Security(): JSX.Element {
  const auth = useRecoilValue(useAuth)
  const [hasPassword, setHasPassword] = useState<boolean | null>(null)
  const [loadErrorKey, setLoadErrorKey] = useState<string | null>(null)

  const loadHasPassword = async () => {
    const result = await fetchHasPassword()
    if (result.ok) {
      setHasPassword(result.data)
      return
    }
    setLoadErrorKey("unknown_error")
  }

  useEffect(() => {
    loadHasPassword()
  }, [])

  return (
    <AppMain aside={<MainMenu />} title={t("login_and_security")}>
      <Section>
        <h2>
          {t(hasPassword ? "password_change_title" : "password_set_title")}
        </h2>
        {loadErrorKey && <Alert type="error" message={t(loadErrorKey)} />}
        {hasPassword === null && !loadErrorKey && <Spin />}
        {hasPassword !== null && (
          <PasswordForm
            hasPassword={hasPassword}
            email={auth.email}
            onSaved={() => setHasPassword(true)}
            onStale={loadHasPassword}
          />
        )}
      </Section>
    </AppMain>
  )
}

type PasswordFormProps = {
  hasPassword: boolean
  email: string
  onSaved: () => void
  // 画面の has_password が古かった（別のタブ・端末で設定された）ときに取り直す
  onStale: () => void
}

const PasswordForm = (props: PasswordFormProps): JSX.Element => {
  const [currentPassword, setCurrentPassword] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [doneKey, setDoneKey] = useState<string | null>(null)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (submitting) return
    setDoneKey(null)

    if (props.hasPassword && !currentPassword) {
      setErrorKey("current_password_required")
      return
    }
    const problem = passwordProblem(password)
    if (problem) {
      setErrorKey(problem)
      return
    }
    if (password !== passwordConfirmation) {
      setErrorKey("password_mismatch")
      return
    }

    setSubmitting(true)
    setErrorKey(null)
    const result = await postProfilePassword({
      currentPassword: props.hasPassword ? currentPassword : undefined,
      password,
      passwordConfirmation,
    })
    setSubmitting(false)

    if (result.ok) {
      setDoneKey(props.hasPassword ? "password_changed" : "password_set_done")
      setCurrentPassword("")
      setPassword("")
      setPasswordConfirmation("")
      props.onSaved()
      return
    }
    if (result.status === 422 && result.errors.current_password) {
      if (!props.hasPassword) {
        // 未設定のつもりで送ったのに API は現在のパスワードを求めた = 画面の状態が古い。
        // 取り直して変更のフォームに切り替える（#446 レビュー S1）
        setErrorKey("password_already_set")
        props.onStale()
        return
      }
      // ロック中（lockable）も API は同じ 422 を返すので区別できない。文言で両方を伝える
      setErrorKey("current_password_invalid")
    } else if (result.status === 422 && result.errors.password) {
      setErrorKey("password_rejected")
    } else {
      setErrorKey("unknown_error")
    }
  }

  return (
    <StyledForm onSubmit={onSubmit} noValidate>
      <p>
        {props.hasPassword
          ? t("password_change_description")
          : t("password_set_description", props.email)}
      </p>
      {props.hasPassword && (
        // 現在のパスワードがわからない（他人が先に登録した等）ときに止まらないように（#446 レビュー S2）
        <Link to={routes.PasswordReset.path}>
          {t("forgot_current_password")}
        </Link>
      )}
      {props.hasPassword && (
        <Input.Password
          aria-label={t("current_password")}
          placeholder={t("current_password")}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
        />
      )}
      <Input.Password
        aria-label={t("new_password")}
        placeholder={t("password_rule", String(PASSWORD_MIN_LENGTH))}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
      />
      <Input.Password
        aria-label={t("password_confirmation")}
        placeholder={t("password_confirmation")}
        value={passwordConfirmation}
        onChange={(e) => setPasswordConfirmation(e.target.value)}
        autoComplete="new-password"
      />
      {errorKey && (
        <Alert
          type="error"
          message={t(errorKey, String(PASSWORD_MIN_LENGTH))}
        />
      )}
      {doneKey && <Alert type="success" message={t(doneKey)} />}
      <Button type="primary" htmlType="submit" loading={submitting}>
        {t(
          props.hasPassword ? "password_change_submit" : "password_set_submit",
        )}
      </Button>
    </StyledForm>
  )
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px 0;
  max-width: 480px;

  h2 {
    font-size: 18px;
    font-weight: 600;
  }
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px 0;
`

export default Security
